import { getData, postData } from "../baseApi";
import { jsonToFormV2 } from "../utils";
import legacy from '../../utils/legacy';
import { adaptListResponse, adaptDetailResponse, adaptMapResponse } from "./responseAdapter";

const ESTATE_EXPLORE_API_ENDPOINT = {
    LIST2: "/sale/filter_sale_list2/",
    LIST2_MOBILE: "/sale/filter_sale_list_mobile/",
    DETAIL: "/sale/filter_sale_detail/",
    DETAIL_MOBILE: "/sale/filter_sale_detail_mobile/",
    LIST: "/sale/filter_sale_list/",
    MAP: "/sale/filter_sale_map/",
    BENCHMARK_ANALYSIS: "/sale/benchmark_analysis/",
    CENTER_PNU: "/polygon/get_pnu_by_latlng2/",
    CENTER_ADDRESS: "/detail/area_here2/",
    GET_REGION: "/sale/get_region/",
    GET_REGION_LATLNG:"/sale/get_region_latlng/",
    GET_REGION_BY_PNU:"/sale/get_region_by_pnu/",

    // v2 — 새 매물탐색 API
    V2_MAP: "/sale/v2/map/",
    V2_LIST: "/sale/v2/list/",
    V2_DETAIL: "/sale/v2/detail/",
};

const _handleError = (error, defaultMessage) => {
    // 개발 환경에서 상세한 에러 정보 출력
    if (process.env.NODE_ENV === 'development') {
        console.error('🚨 API Error Details:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url,
            method: error.config?.method
        });
    }
    
    throw new Error(
        error.response?.data?.message || defaultMessage
    );
};

export const estateExploreService = {

    // 🎯 매물 관련 API 메서드들
    async getList(payload) {
        try {
            
            const form = jsonToFormV2(payload);
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.LIST, form);
        } catch (error) {
            _handleError(error, '매물 목록 조회 중 오류가 발생했습니다.');
        }
    },

    async getList2(payload) {
        try {
            
            const form = jsonToFormV2(payload);
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.LIST2, form);
        } catch (error) {
            _handleError(error, '매물 목록2 조회 중 오류가 발생했습니다.');
        }
    },

    async getList2Mobile(payload) {
        try {
            
            const form = jsonToFormV2(payload);
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.LIST2_MOBILE, form);
        } catch (error) {
            _handleError(error, '모바일 매물 목록 조회 중 오류가 발생했습니다.');
        }
    },

    async getDetail(payload) {
        try {
            
            const form = jsonToFormV2(payload);
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.DETAIL, form);
        } catch (error) {
            _handleError(error, '매물 상세 조회 중 오류가 발생했습니다.');
        }
    },

    async getDetailMobile(payload) {
        try {
            
            const form = jsonToFormV2(payload);
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.DETAIL_MOBILE, form);
        } catch (error) {
            _handleError(error, '모바일 매물 상세 조회 중 오류가 발생했습니다.');
        }
    },

    async getMap(payload, signal) {
        try {
            const form = jsonToFormV2(payload);
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.MAP, form, signal);
        } catch (error) {
            _handleError(error, '지도 데이터 조회 중 오류가 발생했습니다.');
        }
    },

    // ── v2 API ──

    async getMapV2(payload, signal) {
        try {
            const form = jsonToFormV2(payload);
            const res = await postData(ESTATE_EXPLORE_API_ENDPOINT.V2_MAP, form, signal);
            return adaptMapResponse(res);
        } catch (error) {
            _handleError(error, '지도 데이터 조회 중 오류가 발생했습니다.');
        }
    },

    async getListV2(payload) {
        try {
            const form = jsonToFormV2(payload);
            const res = await postData(ESTATE_EXPLORE_API_ENDPOINT.V2_LIST, form);
            return adaptListResponse(res);
        } catch (error) {
            _handleError(error, '매물 목록 조회 중 오류가 발생했습니다.');
        }
    },

    async getDetailV2(payload) {
        try {
            const form = jsonToFormV2(payload);
            const res = await postData(ESTATE_EXPLORE_API_ENDPOINT.V2_DETAIL, form);
            return adaptDetailResponse(res);
        } catch (error) {
            _handleError(error, '동일매물 상세 조회 중 오류가 발생했습니다.');
        }
    },

    // 🌏 지역 관련 메서드들
    region: {
        async getList() {
            try {
                return await getData(ESTATE_EXPLORE_API_ENDPOINT.GET_REGION);
            } catch (error) {
                throw new Error('지역 목록 조회 중 오류가 발생했습니다.');
            }
        },

        async getLatLng(params) {
            try {
                return await getData(ESTATE_EXPLORE_API_ENDPOINT.GET_REGION_LATLNG, params);
            } catch (error) {
                throw new Error('지역 좌표 조회 중 오류가 발생했습니다.');
            }
        },

        async getByPnu(params) {
            try {
                return await getData(ESTATE_EXPLORE_API_ENDPOINT.GET_REGION_BY_PNU, params);
            } catch (error) {
                throw new Error('PNU 기반 지역 조회 중 오류가 발생했습니다.');
            }
        }
    },

    // 🗺️ 위치 관련 메서드들
    location: {
        async getCenterPnu(params) {
            try {
                return await getData(legacy.server_by_hostname('polygon','get_pnu_by_latlng2/'), params);
            } catch (error) {
                throw new Error('PNU 좌표 조회 중 오류가 발생했습니다.');
            }
        },

        async getCenterAddress(params) {
            try {
                return await getData(ESTATE_EXPLORE_API_ENDPOINT.CENTER_ADDRESS, params);
            } catch (error) {
                throw new Error('주소 조회 중 오류가 발생했습니다.');
            }
        }
    },

    // 🎯 벤치마크 관련
    async benchmarkAnalysis() {
        try {
            return await postData(ESTATE_EXPLORE_API_ENDPOINT.BENCHMARK_ANALYSIS, new FormData());
        } catch (error) {
            _handleError(error, '벤치마크 분석 중 오류가 발생했습니다.');
        }
    }
};

// 🔄 기존 호환성 유지를 위한 임시 export (점진적 마이그레이션 후 제거 예정)
export const requestEstateExploreList = (payload) => estateExploreService.getList(payload);
export const requestEstateExploreList2 = (payload) => estateExploreService.getList2(payload);
export const requestEstateExploreDetail = (payload) => estateExploreService.getDetail(payload);
export const requestEstateExploreMap = (payload) => estateExploreService.getMap(payload);
export const requestCenterPnu = (params) => estateExploreService.location.getCenterPnu(params);
export const requestCenterAddress = (params) => estateExploreService.location.getCenterAddress(params);
export const requestRegionByPnu = (params) => estateExploreService.region.getByPnu(params);
export const requestRegion = () => estateExploreService.region.getList();
export const requestRegionLatLng = (params) => estateExploreService.region.getLatLng(params);
export const requestBenchmarkAnalysis = () => estateExploreService.benchmarkAnalysis();
